import { useEffect } from "react";
import Header from "@/pages/home/components/Header";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const staffMembers = [
  {
    name: "Paul Versteeg-Lytwyn",
    role: "Executive Director",
    ext: "Ext. 27",
    email: "paulv@mpfcanada.ca",
  },
  {
    name: "Lisa DeWitt",
    role: "Data Processing Manager",
    ext: "Ext. 22",
    email: "lisad@mpfcanada.ca",
  },
  {
    name: "Humberto Martins",
    role: "Director Pension Benefits",
    ext: "Ext. 23",
    email: "humbert@mpfcanada.ca",
  },
  {
    name: "Nadia Chamale",
    role: "Contract Processing",
    ext: "Ext. 30",
    email: "nadia@mpfcanada.ca",
  },
  {
    name: "Preesin Doorganah",
    role: "Assistant, Pension Benefits",
    ext: "Ext. 29",
    email: "benefits@mpfcanada.ca",
  },
  {
    name: "Thinn Soe",
    role: "Assistant to the Executive Director",
    ext: "Ext. 28",
    email: "thinn@mpfcanada.ca",
  },
  {
    name: "Wahida Hashem",
    role: "Receptionist",
    ext: "Ext. 21",
    email: "reception@mpfcanada.ca",
  },
];

interface ContactRowProps {
  icon: string;
  label: string;
  children: React.ReactNode;
}

function ContactRow({ icon, label, children }: ContactRowProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f4f8fc] flex-shrink-0">
        <i className={`${icon} text-[#1a8fc1] text-sm`}></i>
      </div>
      <div className="min-w-0">
        <h3 className="text-xs font-semibold text-[#0d1f4c] mb-0.5 uppercase tracking-wide">
          {label}
        </h3>
        <div className="text-sm text-gray-500 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

interface StaffCardProps {
  name: string;
  role: string;
  ext: string;
  email: string;
}

function StaffCard({ name, role, ext, email }: StaffCardProps) {
  return (
    <div className="bg-[#f8fafc] rounded-lg border border-gray-200 border-t-2 border-t-[#1a8fc1] shadow hover:shadow-md transition-shadow p-4 flex flex-col">
      <h4 className="text-sm font-semibold text-[#0d1f4c] leading-tight mb-1">
        {name}
      </h4>
      <p className="text-xs text-gray-600 mb-3">
        {role}, {ext}
      </p>
      <div className="mt-auto">
        <a
          href={`mailto:${email}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-3 py-1.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-mail-line text-xs"></i>
          {email}
        </a>
      </div>
    </div>
  );
}

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Navbar />

      <main className="flex-1">
        {/* Hero header */}
        <section className="w-full bg-[#0d1f4c] py-10">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
              <span className="text-[#7ec8e3] text-xs font-semibold tracking-widest uppercase">
                Fund Office
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Contact Us
            </h1>
            <p className="text-base text-white/70 max-w-[600px]">
              Contact the Fund Office for questions about your pension, contributions, benefits, member account access, or general plan information.
            </p>
          </div>
        </section>

        {/* Contact details */}
        <section className="w-full bg-white py-10">
          <div className="max-w-[1200px] mx-auto px-6">
            {/* Main contact details panel */}
            <div className="bg-[#f8fafc] rounded-lg border border-gray-300 border-t-2 border-t-[#1a8fc1] shadow p-5 md:p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                {/* Left column */}
                <div className="space-y-4">
                  <ContactRow icon="ri-map-pin-line" label="Address">
                    200 Yorkland Blvd., Suite 605<br />
                    Toronto, Ontario M2J 5C1
                    <div className="mt-1.5">
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=200%20Yorkland%20Blvd%20Suite%20605%20Toronto%20Ontario%20M2J%205C1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-2.5 py-1 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-map-pin-line text-xs"></i>
                        View Map
                      </a>
                    </div>
                  </ContactRow>

                  <ContactRow icon="ri-phone-line" label="Phone">
                    416-497-4702
                  </ContactRow>

                  <ContactRow icon="ri-printer-line" label="Fax">
                    416-497-4742
                  </ContactRow>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  <ContactRow icon="ri-time-line" label="Office Hours">
                    Monday through Friday<br />
                    9:00 AM to 4:00 PM
                  </ContactRow>

                  <ContactRow icon="ri-phone-line" label="Toll-free">
                    1-888-462-6666
                  </ContactRow>

                  <ContactRow icon="ri-mail-line" label="Email">
                    <a
                      href="mailto:info@mpfcanada.ca"
                      className="text-[#1a8fc1] hover:text-[#157ba6] hover:underline transition-colors cursor-pointer break-all"
                    >
                      info@mpfcanada.ca
                    </a>
                  </ContactRow>
                </div>
              </div>
            </div>

            {/* Staff directory */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-0.5 bg-[#1a8fc1]"></div>
                <h3 className="text-lg font-bold text-[#0d1f4c]">
                  Fund Office Contacts
                </h3>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed max-w-[500px] mb-4">
                Reach out directly to the appropriate Fund Office contact below.
              </p>
            </div>

            {/* Staff cards — row 1 and 2: first 6 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {staffMembers.slice(0, 6).map((staff) => (
                <StaffCard
                  key={staff.email}
                  name={staff.name}
                  role={staff.role}
                  ext={staff.ext}
                  email={staff.email}
                />
              ))}
            </div>

            {/* Staff cards — row 3: last 1 centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-3">
              {staffMembers.slice(6).map((staff) => (
                <div key={staff.email} className="w-full sm:w-[320px]">
                  <StaffCard
                    name={staff.name}
                    role={staff.role}
                    ext={staff.ext}
                    email={staff.email}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}